/**
 * Token-refresh listener API. Mirrors AIM's
 * `onTokenRefreshChange` / `onTokenRefreshComplete` so app-level WebSocket
 * services can reconnect with the rotated token without dropping the session.
 *
 * The listeners live module-scope so subscribers can register before any
 * client is created (matches AIM's import-time subscription pattern).
 */

export type TokenRefreshChangeListener = (isRefreshing: boolean) => void;
export type TokenRefreshCompleteListener = (newToken: string) => void;

const refreshChangeListeners = new Set<TokenRefreshChangeListener>();
const refreshCompleteListeners = new Set<TokenRefreshCompleteListener>();

/**
 * Subscribe to token-refresh state transitions (true → in-flight, false →
 * settled). UI surfaces use this to hold off on a "session expired" flash
 * while a backend-initiated rotation is mid-flight.
 *
 * @returns unsubscribe function
 */
export function onTokenRefreshChange(
  listener: TokenRefreshChangeListener,
): () => void {
  refreshChangeListeners.add(listener);
  return () => {
    refreshChangeListeners.delete(listener);
  };
}

/**
 * Subscribe to refresh-completed events. WebSocket services use this to
 * reconnect with the new token. The new token is passed so subscribers can
 * avoid an extra storage round-trip.
 *
 * @returns unsubscribe function
 */
export function onTokenRefreshComplete(
  listener: TokenRefreshCompleteListener,
): () => void {
  refreshCompleteListeners.add(listener);
  return () => {
    refreshCompleteListeners.delete(listener);
  };
}

export function notifyTokenRefreshChange(isRefreshing: boolean): void {
  for (const listener of refreshChangeListeners) {
    try {
      listener(isRefreshing);
    } catch (error) {
      // Listener faults must not propagate into the transport hook chain.
      console.error('[http-client] token-refresh-change listener faulted:', error);
    }
  }
}

export function notifyTokenRefreshComplete(newToken: string): void {
  for (const listener of refreshCompleteListeners) {
    try {
      listener(newToken);
    } catch (error) {
      console.error('[http-client] token-refresh-complete listener faulted:', error);
    }
  }
}

/**
 * Test hook — drops every registered listener. Production callers should
 * use the unsubscribe function returned by `onTokenRefresh*`.
 */
export function _resetListenersForTests(): void {
  refreshChangeListeners.clear();
  refreshCompleteListeners.clear();
}
